import { ROUTES } from '@/app/routes';
import type { DateRange } from '@/components/ui/FilterBar';

/**
 * What the dashboard shows, and the words it shows it with.
 *
 * The page renders from these lists rather than naming metrics itself, so
 * dropping to the wireframe's four cards, or renaming a row, is an edit here
 * and nowhere else.
 */

export type MetricId =
  | 'totalSupporters'
  | 'totalMembers'
  | 'arcViews'
  | 'contentViews'
  | 'campaignViews'
  | 'callClicks'
  | 'emailClicks'
  | 'guideViews'
  | 'surveyViews'
  | 'surveySubmissions'
  | 'documentViews'
  | 'documentDownloads'
  | 'volunteerClicks'
  | 'volunteerSubmissions'
  | 'fundraisingClicks'
  | 'newSupporters'
  | 'newMembers'
  | 'followedSupporters'
  | 'invitesAccepted'
  | 'notificationsDelivered'
  | 'notificationOpens'
  | 'notificationClicks'
  | 'orgContentViews';

/** The three categories of daily activity, which Overview adds together. */
export type TrendCategory = 'advocacy' | 'content' | 'notifications';

export type TrendTab = 'overview' | TrendCategory;

export type MetricTableId = 'advocacy' | 'supporters';

export type ActivityId =
  | 'activeCampaigns'
  | 'currentGuides'
  | 'openSurveys'
  | 'scheduledNotifications'
  | 'pendingInvitations';

export interface DailyPoint {
  /** `YYYY-MM-DD`. */
  date: string;
  value: number;
}

/**
 * Everything the page needs for one range.
 *
 * Shaped for the page rather than for any API: when the lead's dashboard API
 * lands (Day 79), `useDashboard` maps its reply into this and no screen
 * changes. `source` says whether the numbers are the organization's own.
 */
export interface DashboardData {
  source: 'sample' | 'live';
  range: DateRange;
  previous: DateRange;
  /**
   * The number for the range and for the period before it. The two totals are
   * as at the end of each period; everything else is a count within it.
   */
  metrics: Record<MetricId, { current: number; previous: number }>;
  /** One point per day in the range, for each tab. */
  series: Record<TrendTab, DailyPoint[]>;
  /** What is live right now, whatever the range. */
  activity: Record<ActivityId, number>;
}

export interface MetricDefinition {
  id: MetricId;
  /** The row's name in its table. For a paired row, it names both numbers. */
  label: string;
  /** Present when the metric has a summary card. */
  card?: { label: string; caption: string };
  /** The table it is a row of. The second metric of a pair has none. */
  table: MetricTableId | null;
  /** The metric shown after the slash in the same row. */
  pairedWith?: MetricId;
  /** The trend tab it counts towards, if any. */
  trend: TrendCategory | null;
}

/**
 * Every metric, in the order the cards and each table show them.
 *
 * Req §19: a click is not a completed action. Campaign metrics are named as
 * clicks and views, and fundraising as clicks, never as donations - the
 * wireframe's "Campaign Actions" card is "Call Clicks" and "Email Clicks" here
 * for that reason.
 */
export const METRICS: readonly MetricDefinition[] = [
  {
    id: 'totalSupporters',
    label: 'Total supporters',
    card: { label: 'Total Supporters', caption: 'Following your organization' },
    table: null,
    trend: null,
  },
  {
    id: 'totalMembers',
    label: 'Total members',
    card: { label: 'Total Members', caption: 'Accepted an invitation' },
    table: null,
    trend: null,
  },
  {
    id: 'arcViews',
    label: 'ARC views',
    card: { label: 'ARC Views', caption: 'Opens of your resource center' },
    table: null,
    trend: 'content',
  },
  {
    id: 'contentViews',
    label: 'Page views',
    card: { label: 'Content Views', caption: 'Pages and resources' },
    table: null,
    trend: 'content',
  },
  {
    id: 'campaignViews',
    label: 'Campaign page views',
    table: 'advocacy',
    trend: 'advocacy',
  },
  {
    id: 'callClicks',
    label: 'Call button clicks',
    card: { label: 'Call Clicks', caption: 'Call buttons on campaigns' },
    table: 'advocacy',
    trend: 'advocacy',
  },
  {
    id: 'emailClicks',
    label: 'Email button clicks',
    card: { label: 'Email Clicks', caption: 'Email buttons on campaigns' },
    table: 'advocacy',
    trend: 'advocacy',
  },
  { id: 'guideViews', label: 'Guide views', table: 'advocacy', trend: 'advocacy' },
  {
    id: 'surveyViews',
    label: 'Survey views / submissions',
    table: 'advocacy',
    pairedWith: 'surveySubmissions',
    trend: 'advocacy',
  },
  {
    id: 'surveySubmissions',
    label: 'Survey submissions',
    table: null,
    trend: 'advocacy',
  },
  {
    id: 'documentViews',
    label: 'Document views / downloads',
    table: 'advocacy',
    pairedWith: 'documentDownloads',
    trend: 'content',
  },
  { id: 'documentDownloads', label: 'Document downloads', table: null, trend: null },
  {
    id: 'volunteerClicks',
    label: 'Volunteer clicks / submissions',
    table: 'advocacy',
    pairedWith: 'volunteerSubmissions',
    trend: null,
  },
  {
    id: 'volunteerSubmissions',
    label: 'Volunteer submissions',
    table: null,
    trend: null,
  },
  {
    id: 'fundraisingClicks',
    label: 'Fundraising button clicks',
    table: 'advocacy',
    trend: null,
  },
  { id: 'newSupporters', label: 'New supporters', table: 'supporters', trend: null },
  // Not in the wireframe: added for member growth, which US-161 asks for.
  { id: 'newMembers', label: 'New members', table: 'supporters', trend: null },
  {
    id: 'followedSupporters',
    label: 'Followed supporters',
    table: 'supporters',
    trend: null,
  },
  { id: 'invitesAccepted', label: 'Invites accepted', table: 'supporters', trend: null },
  {
    id: 'notificationsDelivered',
    label: 'Notifications delivered',
    table: 'supporters',
    trend: 'notifications',
  },
  {
    id: 'notificationOpens',
    label: 'Notification opens',
    table: 'supporters',
    trend: 'notifications',
  },
  {
    id: 'notificationClicks',
    label: 'Notification clicks',
    table: 'supporters',
    trend: 'notifications',
  },
  {
    id: 'orgContentViews',
    label: 'Organization content views',
    table: 'supporters',
    trend: 'content',
  },
];

/** The summary cards, in order. */
export const CARD_METRICS = METRICS.filter(
  (metric): metric is MetricDefinition & Required<Pick<MetricDefinition, 'card'>> =>
    metric.card !== undefined,
);

/** The two tables, with the wireframe's captions. */
export const METRIC_TABLES = [
  { id: 'advocacy', caption: 'Advocacy & Resources: performance by feature' },
  { id: 'supporters', caption: 'Supporters & Communications: audience growth and reach' },
] as const satisfies readonly { id: MetricTableId; caption: string }[];

/** A table's rows, in order. */
export function tableRows(table: MetricTableId): MetricDefinition[] {
  return METRICS.filter((metric) => metric.table === table);
}

export const TREND_CATEGORIES: readonly TrendCategory[] = [
  'advocacy',
  'content',
  'notifications',
];

/** The tabs, in order. Overview is the other three added together, day by day. */
export const TREND_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'advocacy', label: 'Advocacy' },
  { id: 'content', label: 'Content' },
  { id: 'notifications', label: 'Notifications' },
] as const satisfies readonly { id: TrendTab; label: string }[];

/** The Current Activity entries, each linking to where that work is managed. */
export const ACTIVITY = [
  { id: 'activeCampaigns', label: 'Active Campaigns', to: ROUTES.campaigns.active },
  { id: 'currentGuides', label: 'Current Guides', to: ROUTES.guides.current },
  { id: 'openSurveys', label: 'Open Surveys', to: ROUTES.surveys.all },
  {
    id: 'scheduledNotifications',
    label: 'Scheduled Notifications',
    to: ROUTES.notifications.scheduled,
  },
  {
    id: 'pendingInvitations',
    label: 'Pending Invitations',
    to: ROUTES.supporters.invitations,
  },
] as const satisfies readonly { id: ActivityId; label: string; to: string }[];
